import * as React from "react"
import type { Session, User } from "@supabase/supabase-js"

import { supabase } from "@/lib/supabase"
import i18n, { resolveAppLanguage } from "@/lib/i18n"

type AuthState = {
  user: User | null
  session: Session | null
  loading: boolean
  signOut: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = React.createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = React.useState(true)
  const [session, setSession] = React.useState<Session | null>(null)
  const [user, setUser] = React.useState<User | null>(null)

  const refresh = React.useCallback(async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase.auth.getSession()
      if (error) throw error
      setSession(data.session)
      const authUser = data.session?.user ?? null
      setUser(authUser)

      // Load language preference from profile
      if (authUser) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("language")
          .eq("id", authUser.id)
          .maybeSingle()
        if (profile?.language) {
          await i18n.changeLanguage(resolveAppLanguage(profile.language))
        } else {
          await i18n.changeLanguage(
            resolveAppLanguage(typeof navigator !== "undefined" ? navigator.language : "en")
          )
        }
      }
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void refresh()

    const { data: subscription } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        setSession(nextSession)
        setUser(nextSession?.user ?? null)
        setLoading(false)

        // Load language on sign-in
        if (nextSession?.user) {
          void supabase
            .from("profiles")
            .select("language")
            .eq("id", nextSession.user.id)
            .maybeSingle()
            .then(({ data: profile }) => {
              if (profile?.language) {
                void i18n.changeLanguage(resolveAppLanguage(profile.language))
              } else {
                void i18n.changeLanguage(
                  resolveAppLanguage(typeof navigator !== "undefined" ? navigator.language : "en")
                )
              }
            })
        }
      }
    )

    return () => {
      subscription.subscription.unsubscribe()
    }
  }, [refresh])

  const signOut = React.useCallback(async () => {
    await supabase.auth.signOut()
  }, [])

  const value = React.useMemo<AuthState>(
    () => ({
      user,
      session,
      loading,
      signOut,
      refresh,
    }),
    [loading, refresh, session, signOut, user]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = React.useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider />")
  return ctx
}

